import { AxiosResponse } from 'axios';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable, DataTableSortEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Image } from 'primereact/image';
import { InputText } from 'primereact/inputtext';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../../../components/loader';
import PrimePaginator from '../../../components/prime-paginator';
import { APIResponseEntity } from '../../../interface/api-responce';
import { DDLEntity, PaginateRespEntity } from '../../../interface';
import { CSMasterListItemEntity, CSMasterListReqEntity, CSMasterTypeDDLEntity, CSMasterUnitDDLEntity, CSStockDetailItemEntity, CSSyncStatusItemEntity } from '../../../interface/settings/cs-master';
import useSingleRowExpansion from '../../../hooks/use-single-row-expansion';
import { APIFrameColorsDDL, APIToExportCSMaster, APIToGetCSSyncStatus, APIToGetActiveCSMasterTypes, APIToGetBOMFormulaVariables, APIToGetBomMappingUOMDDL, APIToGetCSMasters, APIToGetCSMasterUnits, APIToGetFrameColorFilterDDL, APIToGetMappedCSMasterTypes, APIToMapCSMasterFormula, APIToMapCSMasterFrameColor, APIToMapCSMasterType, APIToRemoveCSMasterFormula, APIToRemoveCSMasterFrameColor } from '../../../service/api-service';
import { checkActionAuthorization, downloadCsvFile } from '../../../service/shared.service';
import { emptyMultiSelectSearchMessage, toastOptObj } from '../../../utils/constants';
import { IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import { BOMFormulaFunctionEntity, BOMFormulaVariableEntity } from '../../../interface/settings/bom-mapping';
import { SquareRadical } from 'lucide-react';
import { BOMFormulaEditor, formulaVerdictOf } from '../bom-mapping/bom-formula-editor';
import '../../production-planning/work-order-tracker/work-order-tracker.css';
import './cs-master.css';

const defaultFilter: CSMasterListReqEntity = {
    pageSize: 10,
    pageNumber: 0,
    searchText: '',
    status: '',
    unit: '',
    defaultUom: '',
    salesUom: '',
    csMasterTypeId: '',
    isMapped: '',
    frameColorId: '',
    formula: '',
    sortColumn: 'createdAt',
    sortOrder: '-1'
};

/** the filter bar fields, held apart from the applied request until Apply; the status and mapped chips apply at once */
type CSMasterBarFilterEntity = Pick<CSMasterListReqEntity, 'searchText' | 'formula' | 'csMasterTypeId' | 'frameColorId' | 'defaultUom' | 'unit' | 'salesUom'>;

const barFilterKeys: (keyof CSMasterBarFilterEntity)[] = ['searchText', 'formula', 'csMasterTypeId', 'frameColorId', 'defaultUom', 'unit', 'salesUom'];

const pickBarFilter = (req: CSMasterListReqEntity): CSMasterBarFilterEntity => ({
    searchText: req.searchText || '',
    formula: req.formula || '',
    csMasterTypeId: req.csMasterTypeId || '',
    frameColorId: req.frameColorId || '',
    defaultUom: req.defaultUom || '',
    unit: req.unit || '',
    salesUom: req.salesUom || ''
});

const statusChips: { value: string; label: string; tone: string }[] = [
    { value: '', label: 'All', tone: '' },
    { value: 'active', label: 'Active', tone: 'active-status' },
    { value: 'inactive', label: 'Inactive', tone: 'inactive-status' }
];

const mappedChips: { value: string; label: string; tone: string }[] = [
    { value: '', label: 'All', tone: '' },
    { value: 'yes', label: 'Mapped', tone: 'mapped' },
    { value: 'no', label: 'Not Mapped', tone: 'unmapped' }
];

const formatQtyWithUom = (qty?: number, uom?: string): string => {
    const val = qty ?? 0;
    return uom && uom.trim() !== '' ? `${val}  ${uom.trim()}` : `${val}`;
};

interface CSStockQueryExpansionProps {
    rowData: CSMasterListItemEntity;
    tableWrapperWidth: number;
}

const CSStockQueryExpansion: React.FC<CSStockQueryExpansionProps> = ({ rowData, tableWrapperWidth }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [localWidth, setLocalWidth] = useState<number>(tableWrapperWidth);
    const details: CSStockDetailItemEntity[] = rowData.stockDetails || [];

    useEffect(() => {
        const updateWidth = () => {
            const tableWrapper = containerRef.current?.closest('.p-datatable-wrapper') as HTMLElement | null;
            if (tableWrapper && tableWrapper.clientWidth > 0) {
                setLocalWidth(tableWrapper.clientWidth);
            } else if (containerRef.current?.parentElement) {
                const cardBody = containerRef.current.closest('.card-body') as HTMLElement | null;
                if (cardBody && cardBody.clientWidth > 0) {
                    setLocalWidth(cardBody.clientWidth);
                }
            }
        };

        updateWidth();

        let resizeObserver: ResizeObserver | null = null;
        const tableWrapper = containerRef.current?.closest('.p-datatable-wrapper') as HTMLElement | null;
        const targetToObserve = tableWrapper || containerRef.current?.closest('.card-body');

        if (typeof ResizeObserver !== 'undefined' && targetToObserve) {
            resizeObserver = new ResizeObserver(() => {
                updateWidth();
            });
            resizeObserver.observe(targetToObserve);
        }

        window.addEventListener('resize', updateWidth);
        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    useEffect(() => {
        if (tableWrapperWidth > 0 && localWidth === 0) {
            setLocalWidth(tableWrapperWidth);
        }
    }, [tableWrapperWidth, localWidth]);

    const activeWidth = localWidth || tableWrapperWidth;
    const computedWidthStyle = activeWidth > 0 ? `${activeWidth}px` : 'calc(100vw - 300px)';

    return (
        <div
            ref={containerRef}
            className="wo-tracker-expand cs-master-expand"
            style={{ width: computedWidthStyle, maxWidth: computedWidthStyle }}
        >
            <div className="cs-master-expand-head">
                <span className="wo-tracker-expand-title">STOCK LOCATIONS &middot; {details.length}</span>
                <span className="wo-tracker-expand-title">CS CODE &middot; {rowData.csProductCode || rowData.productCode || rowData.csCode || '-'}</span>
            </div>
            <div className="wo-tracker-expand-items cs-master-expand-items">
                <table>
                    <thead>
                        <tr>
                            <th>Location</th>
                            <th>Available Qty</th>
                            <th>Available Conv Qty</th>
                            <th>In Transit Qty</th>
                            <th>In Transit Conv Qty</th>
                            <th>Size</th>
                        </tr>
                    </thead>
                    <tbody>
                        {details.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="cs-master-expand-empty">
                                    <i className="pi pi-info-circle me-1" />
                                    No stock location records found for this product
                                </td>
                            </tr>
                        ) : (
                            details.map((item, index) => (
                                <tr key={`${item.locationCode || index}-${index}`}>
                                    <td>{item.locationName || item.locationCode || '-'}</td>
                                    <td>{formatQtyWithUom(item.availableQty, item.uom)}</td>
                                    <td>{formatQtyWithUom(item.availableConvQty, item.convUom)}</td>
                                    <td>{formatQtyWithUom(item.inTransitQty, item.uom)}</td>
                                    <td>{formatQtyWithUom(item.inTransitConvQty, item.convUom)}</td>
                                    <td>{item.size || '-'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function CSMaster() {
    const { expandedRows, onRowClick, collapseAll } = useSingleRowExpansion('id');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [records, setRecords] = useState<CSMasterListItemEntity[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [filterReq, setFilterReq] = useState<CSMasterListReqEntity>(defaultFilter);
    const [pendingFilter, setPendingFilter] = useState<CSMasterBarFilterEntity>(pickBarFilter(defaultFilter));
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [unitOptions, setUnitOptions] = useState<CSMasterUnitDDLEntity[]>([]);
    const [defaultUnitOptions, setDefaultUnitOptions] = useState<CSMasterUnitDDLEntity[]>([]);
    const [salesUnitOptions, setSalesUnitOptions] = useState<CSMasterUnitDDLEntity[]>([]);
    const [typeFilterOptions, setTypeFilterOptions] = useState<CSMasterTypeDDLEntity[]>([]);
    const [typeMappingOptions, setTypeMappingOptions] = useState<CSMasterTypeDDLEntity[]>([]);
    const [mappingRow, setMappingRow] = useState<CSMasterListItemEntity | null>(null);
    const [selectedTypeId, setSelectedTypeId] = useState<string>('');
    const [isMappingLoading, setIsMappingLoading] = useState<boolean>(false);
    const [frameColorRow, setFrameColorRow] = useState<CSMasterListItemEntity | null>(null);
    const [selectedFrameColorId, setSelectedFrameColorId] = useState<string>('');
    const [frameColorsDDL, setFrameColorsDDL] = useState<DDLEntity[]>([]);
    const [isFrameColorLoading, setIsFrameColorLoading] = useState<boolean>(false);
    const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
    const [frameColorFilterOptions, setFrameColorFilterOptions] = useState<DDLEntity[]>([]);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [tableWrapperWidth, setTableWrapperWidth] = useState<number>(0);
    const [formulaRow, setFormulaRow] = useState<CSMasterListItemEntity | null>(null);
    const [formulaText, setFormulaText] = useState<string>('');
    const [isFormulaSubmitted, setIsFormulaSubmitted] = useState<boolean>(false);
    const [isFormulaLoading, setIsFormulaLoading] = useState<boolean>(false);
    const [formulaVariables, setFormulaVariables] = useState<BOMFormulaVariableEntity[]>([]);
    const [formulaFunctions, setFormulaFunctions] = useState<BOMFormulaFunctionEntity[]>([]);
    const [formulaUomId, setFormulaUomId] = useState<string>('');
    const [uomOptions, setUomOptions] = useState<DDLEntity[]>([]);
    const [isSyncStatusModalOpen, setIsSyncStatusModalOpen] = useState<boolean>(false);
    const [syncStatusList, setSyncStatusList] = useState<CSSyncStatusItemEntity[]>([]);
    const [isSyncStatusLoading, setIsSyncStatusLoading] = useState<boolean>(false);

    const canMapFrameColor: boolean = checkActionAuthorization('upgrade', 'Allow to Map CS Master Frame Color');
    const canMapFormula: boolean = checkActionAuthorization('upgrade', 'Allow to Map CS Master Formula');
    const canExport: boolean = checkActionAuthorization('upgrade', 'Allow to Export CS Master');
    const canViewSyncStatus: boolean = checkActionAuthorization('upgrade', 'Allow to view sync status of cs collections');

    const formulaVerdict = formulaVerdictOf(formulaText, formulaVariables.map((variable) => variable.key));
    const formulaError: string = IsStringNullEmptyOrUndefined(formulaText.trim())
        ? 'Please enter the quantity formula'
        : (formulaVerdict.parseError || formulaVerdict.previewError);

    const isApplyDisabled: boolean = barFilterKeys.every((key) => (pendingFilter[key] || '') === (filterReq[key] || ''));

    useEffect(() => {
        const updateWidth = () => {
            if (tableContainerRef.current) {
                const wrapper = tableContainerRef.current.querySelector('.p-datatable-wrapper') as HTMLElement | null;
                const targetEl = wrapper || tableContainerRef.current;
                if (targetEl && targetEl.clientWidth > 0) {
                    setTableWrapperWidth(targetEl.clientWidth);
                }
            }
        };

        updateWidth();

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && tableContainerRef.current) {
            resizeObserver = new ResizeObserver(() => {
                updateWidth();
            });
            resizeObserver.observe(tableContainerRef.current);
            const wrapper = tableContainerRef.current.querySelector('.p-datatable-wrapper');
            if (wrapper) {
                resizeObserver.observe(wrapper);
            }
        }

        window.addEventListener('resize', updateWidth);
        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', updateWidth);
        };
    }, [records]);

    useEffect(() => {
        return () => { toast.dismiss(); };
    }, []);

    useEffect(() => {
        if (!canMapFormula) { return; }
        const fetchFormulaVariables = async (): Promise<void> => {
            try {
                const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMFormulaVariables();
                const axiosResp: APIResponseEntity = resp.data;
                const isOk: boolean = resp.status === 200 && axiosResp && axiosResp.status && axiosResp.data;
                setFormulaVariables(isOk ? axiosResp.data.variables || [] : []);
                setFormulaFunctions(isOk ? axiosResp.data.functions || [] : []);
            } catch (error) {
                toast.error('Unable to fetch formula variables', { ...toastOptObj });
            }
        };
        const fetchUOMDDL = async (): Promise<void> => {
            try {
                const resp: AxiosResponse<APIResponseEntity> = await APIToGetBomMappingUOMDDL();
                const axiosResp: APIResponseEntity = resp.data;
                const list: any[] = resp.status === 200 && axiosResp && axiosResp.status && axiosResp.data ? axiosResp.data.data || [] : [];
                setUomOptions(list.map((item: any) => ({ id: item.id, name: item.name })));
            } catch (error) {
                toast.error('Unable to fetch UOM options', { ...toastOptObj });
            }
        };
        fetchFormulaVariables();
        fetchUOMDDL();
    }, [canMapFormula]);

    useEffect(() => {
        if (!canMapFrameColor) { return; }
        const fetchFrameColorsDDL = async (): Promise<void> => {
            try {
                const resp: AxiosResponse<APIResponseEntity> = await APIFrameColorsDDL();
                const axiosResp: APIResponseEntity = resp.data;
                setFrameColorsDDL(resp.status === 200 && axiosResp && axiosResp.status && axiosResp.data ? axiosResp.data.data || [] : []);
            } catch (error) {
                toast.error('Unable to fetch frame colors', { ...toastOptObj });
            }
        };
        fetchFrameColorsDDL();
    }, [canMapFrameColor]);

    useEffect(() => {
        const fetchFilterOptions = async (): Promise<void> => {
            try {
                const [uomResp, typeResp] = await Promise.all([
                    APIToGetCSMasterUnits(),
                    APIToGetMappedCSMasterTypes()
                ]);
                const uomData = uomResp.data?.data?.data || {};
                setUnitOptions(uomData.stockUOM || []);
                setDefaultUnitOptions(uomData.defaultUOM || []);
                setSalesUnitOptions(uomData.salesUOM || []);
                setTypeFilterOptions(typeResp.data?.data?.data || []);
            } catch (error) {
                toast.error('Unable to fetch CS master filter options', { ...toastOptObj });
            }
        };
        fetchFilterOptions();
    }, [refreshKey]);

    useEffect(() => {
        const fetchCSMasters = async (): Promise<void> => {
            setIsLoading(true);
            setRecords([]);
            setTotalRecords(0);
            collapseAll();

            try {
                const resp: AxiosResponse<APIResponseEntity> = await APIToGetCSMasters(filterReq);
                const axiosResp: APIResponseEntity = resp.data;

                if (resp.status === 200 && axiosResp?.status && axiosResp.statusCode === 200) {
                    const paginateResp: PaginateRespEntity = axiosResp.data;
                    setRecords((paginateResp.data || []) as CSMasterListItemEntity[]);
                    setTotalRecords(paginateResp.totalCount || 0);
                } else {
                    toast.error(axiosResp?.message || 'Unable to fetch CS master records', { ...toastOptObj });
                }
            } catch (error) {
                toast.error('Unable to fetch CS master records', { ...toastOptObj });
            } finally {
                setIsLoading(false);
            }
        };

        fetchCSMasters();
    }, [filterReq, refreshKey]);

    useEffect(() => {
        const fetchFrameColorFilterOptions = async (): Promise<void> => {
            try {
                const resp: AxiosResponse<APIResponseEntity> =
                    await APIToGetFrameColorFilterDDL();

                const responseData = resp.data?.data?.data || [];

                setFrameColorFilterOptions(
                    responseData.map((item: any) => ({
                        id: item.Id ?? item.id ?? '',
                        name: item.name ?? ''
                    })).filter((item: DDLEntity) => item.id && item.name)
                );
            } catch (error) {
                toast.error('Unable to fetch frame color filter options', {
                    ...toastOptObj
                });
            }
        };

        fetchFrameColorFilterOptions();
    }, []);

    const updatePendingFilter = (field: keyof CSMasterBarFilterEntity, value: string): void => {
        setPendingFilter(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = (): void => {
        if (isApplyDisabled) { return; }
        collapseAll();
        setFilterReq(prev => ({ ...prev, ...pendingFilter, pageNumber: 0 }));
    };

    const applyChipFilter = (field: 'status' | 'isMapped', value: string): void => {
        collapseAll();
        setFilterReq(prev => ({ ...prev, [field]: value, pageNumber: 0 }));
    };

    const resetFilters = (): void => {
        collapseAll();
        setPendingFilter(pickBarFilter(defaultFilter));
        setFilterReq(prev => ({
            ...defaultFilter,
            pageSize: prev.pageSize
        }));
    };

    const onPageChange = (event: PaginatorPageChangeEvent): void => {
        collapseAll();
        setFilterReq(prev => ({ ...prev, pageSize: event.rows, pageNumber: event.page }));
    };

    const onSort = (event: DataTableSortEvent): void => {
        setFilterReq(prev => ({
            ...prev,
            sortColumn: event.sortField,
            sortOrder: String(event.sortOrder),
            pageNumber: 0
        }));
    };

    const onFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            applyFilters();
        }
    };

    const identityBodyTemplate = (rowData: CSMasterListItemEntity) => (
        <div className="cs-master-identity">
            {rowData.imageUrl ? (
                <div className="cs-master-thumb" onClick={(e) => e.stopPropagation()}>
                    <Image
                        src={rowData.imageUrl}
                        zoomSrc={rowData.imageUrl}
                        alt={rowData.productCode || 'Image'}
                        width="44"
                        height="34"
                        preview
                    />
                </div>
            ) : (
                <div className="cs-master-thumb empty" title="No image available">
                    <i className="pi pi-image" aria-hidden="true" />
                </div>
            )}
            <div className="cs-master-identity-text">
                <span className="cs-master-code">{rowData.productCode || '-'}</span>
                <span className="cs-master-product-id">ID &middot; {rowData.productId || '-'}</span>
            </div>
        </div>
    );

    const descriptionBodyTemplate = (rowData: CSMasterListItemEntity) => (
        <span className="cs-master-description" title={rowData.description || ''}>
            {rowData.description || '-'}
        </span>
    );

    const unitTemplate = (uom?: string) => (
        uom && uom.trim() !== '' ? <span className="cs-master-stat-unit">{uom.trim()}</span> : null
    );

    const qtyTemplate = (qty?: number, uom?: string) => (
        <>
            {typeof qty === 'number' ? qty : 0}
            {unitTemplate(uom)}
        </>
    );

    /** one small stat: a label over its value */
    const statTemplate = (label: string, value: React.ReactNode, isMuted: boolean) => (
        <div className="cs-master-stat">
            <span className="cs-master-stat-label">{label}</span>
            <span className={`cs-master-stat-value ${isMuted ? 'muted' : ''}`}>{value}</span>
        </div>
    );

    const uomBodyTemplate = (rowData: CSMasterListItemEntity) => {
        const defaultUom: string = rowData.defaultUom || '';
        const stockUom: string = rowData.stockUom || '';
        const salesUom: string = rowData.salesUom || '';
        // the three UOMs are usually the same, so one stat says so instead of repeating the value thrice
        const isSameUom: boolean = defaultUom !== '' && defaultUom === stockUom && defaultUom === salesUom;
        return (
            <div className="cs-master-stats uom">
                {isSameUom
                    ? statTemplate('Default · Stock · Sales', defaultUom, false)
                    : (
                        <>
                            {statTemplate('Default', defaultUom || '-', defaultUom === '')}
                            {statTemplate('Stock', stockUom || '-', stockUom === '')}
                            {statTemplate('Sales', salesUom || '-', salesUom === '')}
                        </>
                    )}
            </div>
        );
    };

    /** the conversion figure only adds something when it is in another unit and there is a quantity to convert */
    const convTemplate = (qty: number, uom?: string, convQty?: number, convUom?: string): React.ReactNode => {
        const unit: string = uom ? uom.trim().toLowerCase() : '';
        const convUnit: string = convUom ? convUom.trim().toLowerCase() : '';
        const conv: number = typeof convQty === 'number' ? convQty : 0;
        if (convUnit === '' || convUnit === unit || (qty === 0 && conv === 0)) { return undefined; }
        return qtyTemplate(conv, convUom);
    };

    /** one STOCK line: the label, the quantity in the stock UOM and, past a hairline, its conversion figure */
    const stockRowTemplate = (label: string, qty: number, uom?: string, convQty?: number, convUom?: string) => {
        const conv: React.ReactNode = convTemplate(qty, uom, convQty, convUom);
        return (
            <>
                <span className="cs-master-stock-label">{label}</span>
                <span className={`cs-master-stock-figure ${qty === 0 ? 'muted' : ''}`}>
                    {qtyTemplate(qty, uom)}
                    {conv !== undefined && <span className="cs-master-stock-conv">{conv}</span>}
                </span>
            </>
        );
    };

    const stockBodyTemplate = (rowData: CSMasterListItemEntity) => {
        const availableQty: number = typeof rowData.availableQty === 'number' ? rowData.availableQty : 0;
        const inTransitQty: number = typeof rowData.inTransitQty === 'number' ? rowData.inTransitQty : 0;
        return (
            <div className="cs-master-stock">
                {stockRowTemplate('Available', availableQty, rowData.uom, rowData.availableConvQty, rowData.convUom)}
                {stockRowTemplate('In Transit', inTransitQty, rowData.uom, rowData.inTransitConvQty, rowData.convUom)}
            </div>
        );
    };

    const typeBodyTemplate = (rowData: CSMasterListItemEntity) => (
        IsStringNullEmptyOrUndefined(rowData.csMasterType || '')
            ? <span className="cs-master-empty">no type</span>
            : <span className="cs-master-type">{rowData.csMasterType}</span>
    );

    const frameColorBodyTemplate = (rowData: CSMasterListItemEntity) => (
        <span className={`cs-master-frame-color ${IsStringNullEmptyOrUndefined(rowData.frameColor || '') ? 'cs-master-empty' : ''}`}>
            <i className="pi pi-palette" />
            {rowData.frameColor || 'no frame color'}
        </span>
    );

    const formulaBodyTemplate = (rowData: CSMasterListItemEntity) => (
        <div className="cs-master-formula-cell">
            {IsStringNullEmptyOrUndefined(rowData.formula || '')
                ? <span className="cs-master-empty">no formula</span>
                : <span className="cs-master-formula" title={rowData.formula}>{rowData.formula}</span>}
            <span className={`cs-master-uom-tag ${IsStringNullEmptyOrUndefined(rowData.bomMappingUom || '') ? 'empty' : ''}`}>
                UOM
                <span className="cs-master-uom-tag-value">{rowData.bomMappingUom || 'not set'}</span>
            </span>
        </div>
    );

    /** mapped = a live handle / lock / finish / variant mapping row or a BOM line points at the CS master */
    const statusBodyTemplate = (rowData: CSMasterListItemEntity) => (
        <div className="cs-master-status-cell">
            <span className="cs-master-status">
                <span className={`cs-master-status-dot ${rowData.status === 1 ? '' : 'inactive'}`} />
                {rowData.status === 1 ? 'Active' : 'Inactive'}
            </span>
            <span
                className={`cs-master-pill ${rowData.isMapped ? 'mapped' : 'unmapped'}`}
                title={rowData.isMapped ? 'A module mapping row or a BOM line points at this CS master' : 'No module mapping row or BOM line points at this CS master'}
            >
                {rowData.isMapped ? 'Mapped' : 'Not mapped'}
            </span>
        </div>
    );

    const isRowExpanded = (rowData: CSMasterListItemEntity): boolean => {
        if (!expandedRows) return false;
        if (Array.isArray(expandedRows)) {
            return expandedRows.some((r: any) => (r && r.id ? r.id === rowData.id : r === rowData.id));
        }
        return Boolean((expandedRows as Record<string, any>)[rowData.id]);
    };

    const rowExpansionTemplate = (rowData: CSMasterListItemEntity) => {
        return (
            <CSStockQueryExpansion
                rowData={rowData}
                tableWrapperWidth={tableWrapperWidth}
            />
        );
    };

    const openMappingDialog = async (rowData: CSMasterListItemEntity): Promise<void> => {
        setMappingRow(rowData);
        setSelectedTypeId(rowData.csMasterTypeId || '');
        setTypeMappingOptions([]);
        setIsMappingLoading(true);
        try {
            const resp = await APIToGetActiveCSMasterTypes();
            setTypeMappingOptions(resp.data?.data?.data || []);
        } catch (error) {
            toast.error('Unable to fetch CS master types', { ...toastOptObj });
        } finally {
            setIsMappingLoading(false);
        }
    };

    const closeMappingDialog = (): void => {
        if (isMappingLoading) { return; }
        setMappingRow(null);
        setSelectedTypeId('');
        setTypeMappingOptions([]);
    };

    const saveMapping = async (): Promise<void> => {
        if (!mappingRow || !selectedTypeId) {
            toast.error('Please select CS master type', { ...toastOptObj });
            return;
        }

        setIsMappingLoading(true);
        try {
            const resp = await APIToMapCSMasterType({
                csProductCode: mappingRow.csProductCode,
                csMasterTypeId: selectedTypeId
            });
            if (resp.status === 200 && resp.data?.status) {
                toast.success(resp.data.message, { ...toastOptObj });
                setMappingRow(null);
                setSelectedTypeId('');
                setTypeMappingOptions([]);
                setRefreshKey(prev => prev + 1);
            } else {
                toast.error(resp.data?.message || 'Unable to save CS master type mapping', { ...toastOptObj });
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Unable to save CS master type mapping', { ...toastOptObj });
        } finally {
            setIsMappingLoading(false);
        }
    };

    const openFrameColorDialog = (rowData: CSMasterListItemEntity): void => {
        setFrameColorRow(rowData);
        setSelectedFrameColorId(rowData.frameColorId || '');
    };

    const closeFrameColorDialog = (): void => {
        if (isFrameColorLoading) { return; }
        setFrameColorRow(null);
        setSelectedFrameColorId('');
    };

    const saveFrameColorMapping = async (): Promise<void> => {
        if (!frameColorRow || !selectedFrameColorId) {
            toast.error('Please select frame color', { ...toastOptObj });
            return;
        }

        setIsFrameColorLoading(true);
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToMapCSMasterFrameColor({
                csProductCode: frameColorRow.csProductCode,
                frameColorId: selectedFrameColorId
            });
            const axiosResp: APIResponseEntity = resp.data;
            if (resp.status === 200 && axiosResp && axiosResp.status) {
                toast.success(axiosResp.message, { ...toastOptObj });
                setFrameColorRow(null);
                setSelectedFrameColorId('');
                setRefreshKey(prev => prev + 1);
            } else {
                toast.error(axiosResp && axiosResp.message ? axiosResp.message : 'Unable to save frame color mapping', { ...toastOptObj });
            }
        } catch (error: any) {
            const apiMessage: string = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : '';
            toast.error(apiMessage || 'Unable to save frame color mapping', { ...toastOptObj });
        } finally {
            setIsFrameColorLoading(false);
        }
    };

    const executeRemoveFrameColorMapping = async (csProductCode: string): Promise<void> => {
        setIsFrameColorLoading(true);
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToRemoveCSMasterFrameColor({
                csProductCode
            });
            const axiosResp: APIResponseEntity = resp.data;
            if (resp.status === 200 && axiosResp && axiosResp.status) {
                toast.success(axiosResp.message || 'Frame color mapping removed successfully', { ...toastOptObj });
                setFrameColorRow(null);
                setSelectedFrameColorId('');
                setRefreshKey(prev => prev + 1);
            } else {
                toast.error(axiosResp && axiosResp.message ? axiosResp.message : 'Unable to remove frame color mapping', { ...toastOptObj });
            }
        } catch (error: any) {
            const apiMessage: string = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : '';
            toast.error(apiMessage || 'Unable to remove frame color mapping', { ...toastOptObj });
        } finally {
            setIsFrameColorLoading(false);
        }
    };

    const handleRemoveFrameColorMapping = (): void => {
        if (!frameColorRow || !frameColorRow.csProductCode) { return; }
        const csProductCode: string = frameColorRow.csProductCode;
        const productCode: string = frameColorRow.productCode || 'this CS Master item';
        confirmDialog({
            message: `Are you sure you want to remove the frame color mapping for ${productCode}?`,
            header: 'Remove Frame Color Mapping',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Remove',
            rejectLabel: 'Cancel',
            accept: () => executeRemoveFrameColorMapping(csProductCode)
        });
    };

    const openFormulaDialog = (rowData: CSMasterListItemEntity): void => {
        setFormulaRow(rowData);
        setFormulaText(rowData.formula || '');
        setFormulaUomId(rowData.bomMappingUomId || '');
        setIsFormulaSubmitted(false);
    };

    const closeFormulaDialog = (): void => {
        if (isFormulaLoading) { return; }
        setFormulaRow(null);
        setFormulaText('');
        setFormulaUomId('');
        setIsFormulaSubmitted(false);
    };

    const saveFormulaMapping = async (): Promise<void> => {
        setIsFormulaSubmitted(true);
        if (!formulaRow || !IsStringNullEmptyOrUndefined(formulaError)) {
            return;
        }

        setIsFormulaLoading(true);
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToMapCSMasterFormula({
                csProductCode: formulaRow.csProductCode,
                formula: formulaText.trim(),
                bomMappingUomId: formulaUomId
            });
            const axiosResp: APIResponseEntity = resp.data;
            if (resp.status === 200 && axiosResp && axiosResp.status) {
                toast.success(axiosResp.message, { ...toastOptObj });
                setFormulaRow(null);
                setFormulaText('');
                setFormulaUomId('');
                setRefreshKey(prev => prev + 1);
            } else {
                toast.error(axiosResp && axiosResp.message ? axiosResp.message : 'Unable to save default formula mapping', { ...toastOptObj });
            }
        } catch (error: any) {
            const apiMessage: string = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : '';
            toast.error(apiMessage || 'Unable to save default formula mapping', { ...toastOptObj });
        } finally {
            setIsFormulaLoading(false);
        }
    };

    const executeRemoveFormulaMapping = async (csProductCode: string): Promise<void> => {
        setIsFormulaLoading(true);
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToRemoveCSMasterFormula({ csProductCode });
            const axiosResp: APIResponseEntity = resp.data;
            if (resp.status === 200 && axiosResp && axiosResp.status) {
                toast.success(axiosResp.message, { ...toastOptObj });
                setFormulaRow(null);
                setFormulaText('');
                setFormulaUomId('');
                setRefreshKey(prev => prev + 1);
            } else {
                toast.error(axiosResp && axiosResp.message ? axiosResp.message : 'Unable to remove default formula mapping', { ...toastOptObj });
            }
        } catch (error: any) {
            const apiMessage: string = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : '';
            toast.error(apiMessage || 'Unable to remove default formula mapping', { ...toastOptObj });
        } finally {
            setIsFormulaLoading(false);
        }
    };

    const handleRemoveFormulaMapping = (): void => {
        if (!formulaRow || !formulaRow.csProductCode) { return; }
        const csProductCode: string = formulaRow.csProductCode;
        const productCode: string = formulaRow.productCode || 'this CS Master item';
        confirmDialog({
            message: `Are you sure you want to remove the default formula and UOM of ${productCode}? Existing BOM line items keep the formula and UOM they hold.`,
            header: 'Remove Default Formula',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Remove',
            rejectLabel: 'Cancel',
            accept: () => executeRemoveFormulaMapping(csProductCode)
        });
    };

    const actionBodyTemplate = (rowData: CSMasterListItemEntity) => (
        <div className="cs-master-actions" onClick={(e) => e.stopPropagation()}>
            <Button
                type="button"
                icon="pi pi-tags"
                className="p-button-text p-button-plain cs-master-action-btn"
                tooltip="CS Master Type Mapping"
                tooltipOptions={{ position: 'left' }}
                aria-label="Map CS master type"
                onClick={() => openMappingDialog(rowData)}
            />
            {canMapFrameColor && (
                <Button
                    type="button"
                    className="p-button-text p-button-plain cs-master-action-btn"
                    tooltip="Frame Color Mapping"
                    tooltipOptions={{ position: 'left' }}
                    aria-label="Map frame color"
                    onClick={() => openFrameColorDialog(rowData)}
                >
                    <i className="icon-frame-colors" />
                </Button>
            )}
            {canMapFormula && (
                <Button
                    type="button"
                    className="p-button-text p-button-plain cs-master-action-btn"
                    tooltip="Default Formula Mapping"
                    tooltipOptions={{ position: 'left' }}
                    aria-label="Map default formula"
                    onClick={() => openFormulaDialog(rowData)}
                >
                    <SquareRadical size={18} strokeWidth={2} />
                </Button>
            )}
        </div>
    );

    const handleExportCSMaster = async (): Promise<void> => {
        try {
            setIsExportLoading(true);
            const resp: AxiosResponse<APIResponseEntity> = await APIToExportCSMaster();
            if (resp && resp.status === 200 && resp.data && resp.data.status && resp.data.statusCode === 200) {
                const excelUrl = resp.data.data && resp.data.data.xlsxUrl;
                if (excelUrl && !IsStringNullEmptyOrUndefined(excelUrl)) {
                    const fileName: string = excelUrl.split('/').pop();
                    await downloadCsvFile(excelUrl, fileName);
                }
            } else {
                let errorMessage = 'Unable to export CS Master records';
                if (resp && resp.data && resp.data.message) {
                    errorMessage = resp.data.message;
                }
                toast.error(errorMessage, { ...toastOptObj });
            }
        } catch (error) {
            toast.error('Unable to export CS Master records', { ...toastOptObj });
        } finally {
            setIsExportLoading(false);
        }
    };

    const fetchSyncStatus = async (): Promise<void> => {
        try {
            setIsSyncStatusLoading(true);
            const resp = await APIToGetCSSyncStatus();
            if (resp && resp.status === 200 && resp.data && resp.data.status && resp.data.data) {
                setSyncStatusList(resp.data.data || []);
            } else if (resp && resp.data && resp.data.message) {
                toast.error(resp.data.message, { ...toastOptObj });
            }
        } catch (error: any) {
            toast.error(error?.message || 'Unable to fetch sync status', { ...toastOptObj });
        } finally {
            setIsSyncStatusLoading(false);
        }
    };

    const openSyncStatusModal = (): void => {
        setIsSyncStatusModalOpen(true);
        fetchSyncStatus();
    };

    const closeSyncStatusModal = (): void => {
        setIsSyncStatusModalOpen(false);
    };

    const unitDropdown = (field: 'defaultUom' | 'unit' | 'salesUom', options: CSMasterUnitDDLEntity[], placeholder: string) => (
        <Dropdown
            value={IsStringNullEmptyOrUndefined(pendingFilter[field] || '') ? null : pendingFilter[field]}
            options={options}
            optionLabel="units"
            optionValue="units"
            onChange={(event) => updatePendingFilter(field, event.value || '')}
            placeholder={placeholder}
            filter
            showClear
            resetFilterOnHide
            emptyFilterMessage={emptyMultiSelectSearchMessage}
            className="w-full md:w-10rem"
        />
    );

    return (
        <>
            <Loader isLoading={isLoading || isMappingLoading || isFrameColorLoading || isFormulaLoading || isExportLoading} />
            <div className="content-wrapper">
                <div className="card card-rounded">
                    <div className="card-body">
                        <div className="row align-items-center mb-3">
                            <div className="col-md-6">
                                <h4 className="card-title mb-0">CS Master</h4>
                            </div>
                            <div className="col-md-6 d-flex flex-column align-items-end">
                                <div className="d-flex align-items-center gap-2">
                                    {canViewSyncStatus && (
                                        <button
                                            type="button"
                                            className="btn btn-primary font-weight-medium d-inline-flex align-items-center gap-1"
                                            onClick={openSyncStatusModal}
                                            title="View sync status of CS collections"
                                        >
                                            <i className="pi pi-sync"></i>
                                            <span>Sync Status</span>
                                        </button>
                                    )}
                                    {canExport && (
                                        <button
                                            type="button"
                                            className="btn btn-success font-weight-medium"
                                            onClick={handleExportCSMaster}
                                            disabled={isExportLoading || isLoading}
                                        >
                                            Export CS Master
                                        </button>
                                    )}
                                </div>
                                <span
                                    className="resetFilters text-primary cursor-pointer mt-2"
                                    style={{ whiteSpace: 'nowrap' }}
                                    onClick={resetFilters}
                                >
                                    Reset Filters
                                </span>
                            </div>
                        </div>

                        <div className="cs-master-chip-groups">
                            <div className="wo-tracker-chip-row">
                                <span className="wo-tracker-chip-row-label">Status</span>
                                {statusChips.map((chip) => (
                                    <span
                                        key={chip.value}
                                        className={`wo-tracker-chip ${(filterReq.status || '') === chip.value ? 'active' : ''}`}
                                        onClick={() => applyChipFilter('status', chip.value)}
                                    >
                                        {chip.tone !== '' && <span className={`wo-tracker-chip-dot ${chip.tone}`} />}
                                        {chip.label}
                                    </span>
                                ))}
                            </div>
                            <div className="wo-tracker-chip-row">
                                <span className="wo-tracker-chip-row-label">Mapped</span>
                                {mappedChips.map((chip) => (
                                    <span
                                        key={chip.value}
                                        className={`wo-tracker-chip ${(filterReq.isMapped || '') === chip.value ? 'active' : ''}`}
                                        onClick={() => applyChipFilter('isMapped', chip.value)}
                                    >
                                        {chip.tone !== '' && <span className={`wo-tracker-chip-dot ${chip.tone}`} />}
                                        {chip.label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="d-flex align-items-end gap-2 flex-wrap">
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>SEARCH</label>
                                <span className="cs-master-search">
                                    <i className="pi pi-search" />
                                    <InputText
                                        value={pendingFilter.searchText || ''}
                                        placeholder="CS code, product ID or description"
                                        onChange={(event) => updatePendingFilter('searchText', event.target.value)}
                                        onKeyDown={onFilterKeyDown}
                                        className="w-full md:w-20rem"
                                    />
                                </span>
                            </div>
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>CS MASTER TYPE</label>
                                <Dropdown
                                    value={IsStringNullEmptyOrUndefined(pendingFilter.csMasterTypeId || '') ? null : pendingFilter.csMasterTypeId}
                                    options={typeFilterOptions}
                                    optionLabel="type"
                                    optionValue="_id"
                                    onChange={(event) => updatePendingFilter('csMasterTypeId', event.value || '')}
                                    placeholder="Type"
                                    filter
                                    showClear
                                    resetFilterOnHide
                                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                                    className="w-full md:w-12rem"
                                />
                            </div>
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>FRAME COLOR</label>
                                <Dropdown
                                    value={IsStringNullEmptyOrUndefined(pendingFilter.frameColorId || '') ? null : pendingFilter.frameColorId}
                                    options={frameColorFilterOptions}
                                    optionLabel="name"
                                    optionValue="id"
                                    onChange={(event) => updatePendingFilter('frameColorId', event.value || '')}
                                    placeholder="Frame color"
                                    filter
                                    showClear
                                    resetFilterOnHide
                                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                                    className="w-full md:w-12rem"
                                />
                            </div>
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>DEFAULT UOM</label>
                                {unitDropdown('defaultUom', defaultUnitOptions, 'Default UOM')}
                            </div>
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>STOCK UOM</label>
                                {unitDropdown('unit', unitOptions, 'Stock UOM')}
                            </div>
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>SALES UOM</label>
                                {unitDropdown('salesUom', salesUnitOptions, 'Sales UOM')}
                            </div>
                            <div className="form-group mb-0 wo-tracker-filter-field">
                                <label>DEFAULT FORMULA</label>
                                <InputText
                                    value={pendingFilter.formula || ''}
                                    placeholder="Formula contains"
                                    onChange={(event) => updatePendingFilter('formula', event.target.value)}
                                    onKeyDown={onFilterKeyDown}
                                    className="w-full md:w-12rem"
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={applyFilters}
                                disabled={isApplyDisabled}
                            >
                                Apply
                            </button>
                        </div>

                        <div className="wo-tracker-table-count">
                            {totalRecords} CS master item(s)
                        </div>

                        <div className="wo-tracker-table cs-master-table" ref={tableContainerRef}>
                            <DataTable
                                value={records}
                                lazy={true}
                                scrollable
                                dataKey="id"
                                expandedRows={expandedRows}
                                onRowClick={onRowClick}
                                rowClassName={(rowData: CSMasterListItemEntity) => (isRowExpanded(rowData) ? 'cs-master-expanded-row' : '')}
                                rowExpansionTemplate={rowExpansionTemplate}
                                sortField={filterReq.sortColumn}
                                sortOrder={filterReq.sortOrder ? parseInt(filterReq.sortOrder) as 1 | -1 : -1}
                                onSort={onSort}
                                emptyMessage="No CS master records found"
                                rowHover
                            >
                                <Column field="productCode" header="CS MASTER" sortable body={identityBodyTemplate} />
                                <Column field="description" header="DESCRIPTION" sortable body={descriptionBodyTemplate} />
                                <Column field="defaultUom" header="UOM" sortable body={uomBodyTemplate} />
                                <Column field="availableQty" header="STOCK" sortable body={stockBodyTemplate} />
                                <Column field="csMasterType" header="CS MASTER TYPE" sortable body={typeBodyTemplate} />
                                <Column field="frameColor" header="FRAME COLOR" sortable body={frameColorBodyTemplate} />
                                <Column field="formula" header="DEFAULT FORMULA" sortable body={formulaBodyTemplate} />
                                <Column field="status" header="STATUS" sortable body={statusBodyTemplate} />
                                <Column header="ACTION" frozen alignFrozen="right" body={actionBodyTemplate} />
                            </DataTable>

                            {totalRecords > 0 && (
                                <PrimePaginator
                                    onPageChange={onPageChange}
                                    pageNumber={filterReq.pageNumber}
                                    pageSize={filterReq.pageSize}
                                    totalRecords={totalRecords}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                visible={mappingRow !== null}
                header="CS Master Type Mapping"
                modal
                draggable={false}
                resizable={false}
                closable={!isMappingLoading}
                style={{ width: '440px', maxWidth: '95vw' }}
                onHide={closeMappingDialog}
                footer={(
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-cancel text-dark m-0"
                            onClick={closeMappingDialog}
                            disabled={isMappingLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary m-0"
                            onClick={saveMapping}
                            disabled={isMappingLoading || !selectedTypeId}
                        >
                            Save Mapping
                        </button>
                    </div>
                )}
            >
                <div className="row mb-2">
                    <div className="col-6 mb-2">
                        <label className="text-muted small mb-1 d-block">PRODUCTION ID</label>
                        <div className="font-weight-medium">{mappingRow?.productId || '-'}</div>
                    </div>
                    <div className="col-6 mb-2">
                        <label className="text-muted small mb-1 d-block">CS CODE</label>
                        <div className="font-weight-medium">{mappingRow?.productCode || '-'}</div>
                    </div>
                    <div className="col-12 mb-2">
                        <label className="text-muted small mb-1 d-block">DESCRIPTION</label>
                        <div className="font-weight-medium text-break">{mappingRow?.description || '-'}</div>
                    </div>
                    <div className="col-12 mb-2">
                        <label className="text-muted small mb-1 d-block">STOCK UOM</label>
                        <div className="font-weight-medium">{mappingRow?.stockUom || '-'}</div>
                    </div>
                </div>
                <div className="form-group mb-0">
                    <label>CS MASTER TYPE <sup className="mandatory">*</sup></label>
                    <Dropdown
                        value={selectedTypeId}
                        options={typeMappingOptions}
                        optionLabel="type"
                        optionValue="_id"
                        onChange={(event) => setSelectedTypeId(event.value)}
                        placeholder="Select CS Master Type"
                        filter
                        resetFilterOnHide
                        disabled={isMappingLoading}
                        className="w-100"
                    />
                </div>
            </Dialog>

            <Dialog
                visible={frameColorRow !== null}
                header="Frame Color Mapping"
                modal
                draggable={false}
                resizable={false}
                closable={!isFrameColorLoading}
                style={{ width: '480px', maxWidth: '95vw' }}
                onHide={closeFrameColorDialog}
                footer={(
                    <div className="d-flex justify-content-between align-items-center w-100">
                        {frameColorRow && !IsStringNullEmptyOrUndefined(frameColorRow.frameColorId || "") ? (
                            <button
                                type="button"
                                className="btn btn-danger m-0 d-inline-flex align-items-center"
                                onClick={handleRemoveFrameColorMapping}
                                disabled={isFrameColorLoading}
                                title="Remove current color mapping"
                            >
                                <i className="pi pi-trash me-1" style={{ fontSize: '13px' }} />
                                <span>Remove Mapping</span>
                            </button>
                        ) : <span />}
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-cancel text-dark m-0"
                                onClick={closeFrameColorDialog}
                                disabled={isFrameColorLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary m-0"
                                onClick={saveFrameColorMapping}
                                disabled={isFrameColorLoading || !selectedFrameColorId}
                            >
                                Save Mapping
                            </button>
                        </div>
                    </div>
                )}
            >
                <div className="row mb-2">
                    <div className="col-6 mb-2">
                        <label className="text-muted small mb-1 d-block">PRODUCTION ID</label>
                        <div className="font-weight-medium">{frameColorRow && frameColorRow.productId ? frameColorRow.productId : '-'}</div>
                    </div>
                    <div className="col-6 mb-2">
                        <label className="text-muted small mb-1 d-block">CS CODE</label>
                        <div className="font-weight-medium">{frameColorRow && frameColorRow.productCode ? frameColorRow.productCode : '-'}</div>
                    </div>
                    <div className="col-12 mb-2">
                        <label className="text-muted small mb-1 d-block">DESCRIPTION</label>
                        <div className="font-weight-medium text-break">{frameColorRow && frameColorRow.description ? frameColorRow.description : '-'}</div>
                    </div>
                </div>
                <div className="form-group mb-0">
                    <label>FRAME COLOR <sup className="mandatory">*</sup></label>
                    <Dropdown
                        value={selectedFrameColorId}
                        options={frameColorsDDL}
                        optionLabel="name"
                        optionValue="id"
                        onChange={(event) => setSelectedFrameColorId(event.value)}
                        placeholder="Select Frame Color"
                        filter
                        showClear
                        resetFilterOnHide
                        disabled={isFrameColorLoading}
                        className="w-100"
                    />
                </div>
            </Dialog>

            <Dialog
                visible={formulaRow !== null}
                header={(
                    <div>
                        <div className="bom-dialog-title">Default Formula Mapping</div>
                        <div className="bom-dialog-sub">{formulaRow ? [formulaRow.productCode, formulaRow.description].filter((part) => !IsStringNullEmptyOrUndefined(part)).join(' · ') : ''}</div>
                    </div>
                )}
                className="bom-dialog"
                modal
                draggable={false}
                resizable={false}
                closable={!isFormulaLoading}
                style={{ width: '960px', maxWidth: '96vw' }}
                onHide={closeFormulaDialog}
                footer={(
                    <div className="d-flex justify-content-between align-items-center w-100">
                        {formulaRow && !IsStringNullEmptyOrUndefined(formulaRow.formula || '') ? (
                            <button
                                type="button"
                                className="btn btn-danger m-0 d-inline-flex align-items-center"
                                onClick={handleRemoveFormulaMapping}
                                disabled={isFormulaLoading}
                                title="Remove the default formula"
                            >
                                <i className="pi pi-trash me-1" style={{ fontSize: '13px' }} />
                                <span>Remove Mapping</span>
                            </button>
                        ) : <span />}
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-cancel text-dark m-0"
                                onClick={closeFormulaDialog}
                                disabled={isFormulaLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary font-weight-medium m-0"
                                onClick={saveFormulaMapping}
                                disabled={isFormulaLoading || !IsStringNullEmptyOrUndefined(formulaVerdict.previewError)}
                            >
                                Save Mapping
                            </button>
                        </div>
                    </div>
                )}
            >
                {/* Row 1: the CS master the default is for, laid out like the BOM dialog's item row */}
                <div className="row mb-3 align-items-center">
                    <div className="col-12 col-md-6">
                        <div className="d-flex align-items-start" style={{ gap: '20px' }}>
                            <div style={{ flex: '0 0 auto', minWidth: '120px' }}>
                                <label className="text-muted small font-weight-bold d-block mb-1">PRODUCTION ID</label>
                                <div className="font-weight-medium text-dark" style={{ fontSize: '13px' }}>{formulaRow && formulaRow.productId ? formulaRow.productId : '-'}</div>
                            </div>
                            <div style={{ flex: '0 0 auto', minWidth: '120px' }}>
                                <label className="text-muted small font-weight-bold d-block mb-1">ITEM CODE</label>
                                <div className="font-weight-medium text-dark" style={{ fontSize: '13px' }}>{formulaRow && formulaRow.productCode ? formulaRow.productCode : '-'}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-6">
                        <label className="text-muted small font-weight-bold d-block mb-1">DESCRIPTION</label>
                        <div
                            className="font-weight-medium text-dark"
                            style={{ fontSize: '13px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3' }}
                            title={formulaRow && formulaRow.description ? formulaRow.description : ''}
                        >
                            {formulaRow && formulaRow.description ? formulaRow.description : '-'}
                        </div>
                    </div>
                </div>

                <BOMFormulaEditor
                    value={formulaText}
                    onChange={setFormulaText}
                    variables={formulaVariables}
                    functions={formulaFunctions}
                    isSubmitted={isFormulaSubmitted}
                    error={formulaError}
                    required
                    hint={(
                        <small className="text-muted d-block mt-1">
                            BOM line items mapped without a formula get a copy of it. A change here reaches every line item of this CS master, Manual and Reference, including ones edited on the BOM mapping side.
                        </small>
                    )}
                    beforeFormula={(
                        <div className="form-group mb-3">
                            <label className="text-muted small font-weight-bold d-block mb-1">UOM</label>
                            <Dropdown
                                value={IsStringNullEmptyOrUndefined(formulaUomId) ? null : formulaUomId}
                                options={uomOptions}
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Select UOM"
                                filter
                                showClear
                                emptyFilterMessage={emptyMultiSelectSearchMessage}
                                className="w-100"
                                onChange={(e) => setFormulaUomId(e.value || '')}
                            />
                            <small className="text-muted d-block mt-1">New BOM line items of this CS master start with it and a change here moves every line item of this CS master to it. Clearing it leaves them as they are.</small>
                        </div>
                    )}
                />
            </Dialog>

            <Dialog
                header={(
                    <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                        <div className="d-flex align-items-center gap-2">
                            <i className="pi pi-sync text-primary" style={{ fontSize: '1.25rem' }} />
                            <span className="font-weight-bold" style={{ fontSize: '1.1rem' }}>CS Collections Sync Status</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                            onClick={fetchSyncStatus}
                            disabled={isSyncStatusLoading}
                            title="Refresh status"
                        >
                            <i className={`pi pi-refresh ${isSyncStatusLoading ? 'pi-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                )}
                visible={isSyncStatusModalOpen}
                style={{ width: '650px', maxWidth: '95vw' }}
                onHide={closeSyncStatusModal}
                closable
                draggable={false}
                resizable={false}
                footer={(
                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary m-0"
                            onClick={closeSyncStatusModal}
                        >
                            Close
                        </button>
                    </div>
                )}
            >
                {isSyncStatusLoading && syncStatusList.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }} />
                    </div>
                ) : (
                    <div className="table-responsive mt-2">
                        <table className="table table-bordered table-hover mb-0">
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ width: '50%' }}>Collection / Table</th>
                                    <th style={{ width: '50%' }}>Last Synced At (IST)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {syncStatusList.length === 0 ? (
                                    <tr>
                                        <td colSpan={2} className="text-center text-muted py-4">
                                            No sync status records found
                                        </td>
                                    </tr>
                                ) : (
                                    syncStatusList.map((item) => (
                                        <tr key={item.key}>
                                            <td>
                                                <div className="font-weight-bold text-dark">{item.name}</div>
                                                <small className="text-muted font-monospace">{item.collectionName}</small>
                                            </td>
                                            <td>
                                                <div className="text-dark font-weight-medium">
                                                    {item.lastSuccessAtFormatted !== '-' ? item.lastSuccessAtFormatted : 'Never Synced'}
                                                </div>
                                                {item.lastSuccessAtFormatted !== '-' && (
                                                    <small className="text-muted d-block">{item.timeAgo}</small>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </Dialog>

            <ConfirmDialog />
        </>
    );
}

export default CSMaster;
